import { createContext, useContext } from "react";
import type { ReactNode } from "react";

import type { RobotState } from "../modules/teleop-state";

/**
 * React context carrying the per-session `RobotState` instance to descendants.
 *
 * The page owns the session (and thus the state) via `useOperatorSession`, wraps its
 * subtree in `<RobotStateProvider robotState={robotState}>` (the `robotState` returned
 * alongside `session`/`commands`), and any hook or component below reads
 * `useRobotState()` instead of a global singleton.
 * Each page mount gets an independent `createRobotState()` instance, so robot business
 * state is scoped per session and never leaks across sessions/tests.
 */
export const RobotStateContext = createContext<RobotState | null>(null);

export interface RobotStateProviderProps {
  robotState: RobotState;
  children: ReactNode;
}

export function useRobotState(): RobotState {
  const robotState = useContext(RobotStateContext);
  if (!robotState) {
    throw new Error("useRobotState must be used within a <RobotStateProvider>");
  }
  return robotState;
}
